import React from "react";
import PropTypes from "prop-types";
import { IoIosArrowForward } from "react-icons/io";
import { FaArrowLeft, FaArrowRight, FaCheck } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const Stepper2 = ({
  steps = [],
  currentStep,
  setCurrentStep,
  finishStep,
  onStepChange,
}) => {
  const isCompactMode = steps.length > 5;

  const handleStepChange = (newStep) => {
    if (steps[newStep]?.onChange) steps[newStep].onChange();
    setCurrentStep(newStep);
    onStepChange?.(newStep);
  };

  const nextStep = async () => {
    const step = steps[currentStep];
    if (step?.onNext) {
      const canContinue = await step.onNext();
      if (!canContinue) return;
    }
    if (currentStep < steps.length - 1) {
      handleStepChange(currentStep + 1);
    } else {
      finishStep?.();
    }
  };

  const prevStep = async () => {
    const step = steps[currentStep];
    if (step?.onBack) {
      const canGoBack = await step.onBack();
      if (!canGoBack) return;
    }
    if (currentStep > 0) handleStepChange(currentStep - 1);
  };

  const getShowBack = () => {
    if (currentStep === 0) return false;
    const step = steps[currentStep];
    return typeof step?.showBack === "function"
      ? step.showBack()
      : step?.showBack ?? true;
  };

  const getShowNext = () => {
    if (currentStep === steps.length - 1) return false;
    const step = steps[currentStep];
    return typeof step?.showNext === "function"
      ? step.showNext()
      : step?.showNext ?? true;
  };

  const getShowFinish = () => {
    if (currentStep !== steps.length - 1) return false;
    const step = steps[currentStep];
    return typeof step?.showNext === "function"
      ? step.showNext()
      : step?.showNext ?? true;
  };

  return (
    <div className="flex w-full flex-col">
      <div className="relative w-full">
        <div className="flex items-center justify-between gap-4 px-2 py-4">
          <motion.div
            layout
            className="no-scrollbar flex flex-1 items-center gap-4 overflow-x-auto pr-4"
          >
            {steps.map((step, index) => {
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <motion.div
                  layout
                  key={index}
                  className="flex shrink-0 items-center space-x-2"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center space-x-2">
                    <motion.div
                      layout
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-all duration-300
                      ${isActive ? "bg-primary text-white" : "bg-gray-200 text-gray-600"}`}
                    >
                      {isCompleted ? (
                        <motion.span
                          key="check"
                          initial={{ rotate: -90, scale: 0 }}
                          animate={{ rotate: 0, scale: 1 }}
                          exit={{ rotate: 90, scale: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="inline-block"
                        >
                          <FaCheck />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="index"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {index + 1}
                        </motion.span>
                      )}
                    </motion.div>
                    {(!isCompactMode || isActive) && (
                      <span
                        className={`whitespace-nowrap text-sm ${isActive ? "font-semibold text-primary" : "text-gray-700"}`}
                      >
                        {step.title}
                      </span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <IoIosArrowForward className="mx-2 text-xl text-gray-400" />
                  )}
                </motion.div>
              );
            })}
          </motion.div>

          <div className="flex shrink-0 gap-2 pl-4">
            {getShowBack() && (
              <button
                type="button"
                className="rounded bg-gray-300 px-3 py-2 text-sm disabled:opacity-50"
                onClick={prevStep}
                disabled={currentStep === 0}
              >
                <span className="hidden sm:inline">Atrás</span>
                <span className="sm:hidden">←</span>
              </button>
            )}
            {getShowNext() && (
              <button
                type="button"
                className="rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-50"
                onClick={nextStep}
                disabled={currentStep >= steps.length - 1}
              >
                <span className="hidden sm:inline">Siguiente</span>
                <span className="sm:hidden">→</span>
              </button>
            )}
            {getShowFinish() && (
              <button
                type="button"
                className="rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-50"
                onClick={nextStep}
                disabled={currentStep === steps.length}
              >
                <span className="hidden sm:inline">Finalizar</span>
                <span className="sm:hidden">→</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-[150px] w-full px-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            {steps[currentStep]?.body ?? (
              <div className="py-8 text-center italic text-gray-500">
                No hay contenido para este paso.
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Stepper2;

Stepper2.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired, // Título del paso
      body: PropTypes.node, // Contenido del paso
      onNext: PropTypes.func, // Función llamada al avanzar
    }),
  ).isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired, // Título del paso
      body: PropTypes.node, // Contenido del paso
      onNext: PropTypes.func, // Función llamada al avanzar
    }),
  ).isRequired,
  initialStep: PropTypes.number, // Paso inicial
  currentStep: PropTypes.number, // Paso controlado externamente
  onStepChange: PropTypes.func, // Callback al cambiar de paso
  setCurrentStep: PropTypes.func, // Función para actualizar el paso externo
  finishStep: PropTypes.func
};
